import { useEffect } from "react";
import { useUIStore } from "@/stores/ui.store";

const EXTRA_DARK_THEMES  = ["dark-midnight", "dark-graphite", "dark-abyss", "dark-forest", "dark-dusk", "dark-expanse"] as const;
const EXTRA_LIGHT_THEMES = ["light-nova", "light-ivory", "light-blush", "light-sage", "light-sand", "light-mist"] as const;

// Maps FontFamily value → CSS font-family stack
const FONT_STACKS: Record<string, string> = {
  system:         "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  inter:          "'Inter', ui-sans-serif, sans-serif",
  ubuntu:         "'Ubuntu', ui-sans-serif, sans-serif",
  geist:          "'Geist', 'Inter', ui-sans-serif, sans-serif",
  "jetbrains-mono":"'JetBrains Mono', ui-monospace, monospace",
  "plus-jakarta":  "'Plus Jakarta Sans', ui-sans-serif, sans-serif",
  "dm-sans":       "'DM Sans', ui-sans-serif, sans-serif",
  nunito:          "'Nunito', ui-sans-serif, sans-serif",
  outfit:          "'Outfit', ui-sans-serif, sans-serif",
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, accentColor, radius, fontFamily, sidebarStyle } = useUIStore();

  useEffect(() => {
    const root = document.documentElement;

    // Apply accent color
    root.setAttribute("data-theme", accentColor);

    // Apply radius
    root.style.setProperty("--radius", `${radius}rem`);

    // Apply font family
    root.style.setProperty("font-family", FONT_STACKS[fontFamily] ?? FONT_STACKS.system);

    // Apply dark/light mode + extra dark theme variant
    const applyMode = (isDark: boolean) => {
      root.classList.toggle("dark", isDark);
    };

    const isExtraDark  = (EXTRA_DARK_THEMES  as readonly string[]).includes(theme);
    const isExtraLight = (EXTRA_LIGHT_THEMES as readonly string[]).includes(theme);

    if (isExtraDark) {
      root.classList.add("dark");
      root.removeAttribute("data-light-theme");
      root.setAttribute("data-dark-theme", theme.replace("dark-", ""));
      return () => {
        root.removeAttribute("data-dark-theme");
      };
    }

    if (isExtraLight) {
      root.classList.remove("dark");
      root.removeAttribute("data-dark-theme");
      root.setAttribute("data-light-theme", theme.replace("light-", ""));
      return () => {
        root.removeAttribute("data-light-theme");
      };
    }

    // Clear any extra theme attributes
    root.removeAttribute("data-dark-theme");
    root.removeAttribute("data-light-theme");

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      applyMode(mq.matches);
      const handler = (e: MediaQueryListEvent) => applyMode(e.matches);
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }

    applyMode(theme === "dark");
  }, [theme, accentColor, radius, fontFamily]);

  useEffect(() => {
    document.documentElement.setAttribute("data-sidebar-style", sidebarStyle);
  }, [sidebarStyle]);

  return <>{children}</>;
}
